import { useEffect, useState } from "react";
import { FileText, Download } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Steering-committee PDF: pick sections, see the plain-language summary first. Partner notes and individual stakeholder assessments are never included.
export default function ReportDialog({ change, open, onClose }) {
  const [p, setP] = useState(null);
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!open) return; setP(null); api.get(`/reports/change/${change.id}/preview`).then((r) => { setP(r.data); setSel(r.data.sections.filter((s) => s.count > 0).map((s) => s.key)); }).catch((e) => toast.error(errMsg(e))); }, [open, change.id]);
  const gen = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/reports/change/${change.id}`, { sections: sel }, { responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = `${change.ref}-report.pdf`; a.click(); URL.revokeObjectURL(url);
      toast.success("Report downloaded"); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-w-xl" data-testid="report-dialog">
        <DialogHeader><DialogTitle className="font-head text-2xl font-extrabold flex items-center gap-2"><FileText size={18} /> Readiness &amp; engagement report</DialogTitle></DialogHeader>
        {!p ? <p className="text-sm text-muted-foreground">Preparing preview…</p> : (
          <div className="space-y-4 text-sm">
            <div className="rounded-xl bg-muted p-3"><div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Executive summary preview</div>{p.summary.map((s, i) => <p key={i} className="text-subtle mt-1" data-testid="report-summary-line">{s}</p>)}</div>
            <div className="grid sm:grid-cols-2 gap-2" role="group" aria-label="Sections to include">
              {p.sections.map((s) => (
                <label key={s.key} className={`flex items-center gap-2 rounded-xl border px-3 py-2 ${sel.includes(s.key) ? "border-ink" : "border-border"} ${s.count === 0 ? "opacity-60" : ""}`}>
                  <input type="checkbox" data-testid={`report-section-${s.key}`} checked={sel.includes(s.key)} onChange={(e) => setSel(e.target.checked ? [...sel, s.key] : sel.filter((k) => k !== s.key))} className="h-4 w-4" />
                  <span className="flex-1 font-semibold text-foreground">{s.label}</span><span className="text-[11px] text-muted-foreground">{s.count === 0 ? "empty" : s.count}</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-muted-foreground" data-testid="report-branding-note">Branded as <span className="font-bold">{p.branding}</span> · Powered by ChangeOS on every page · never includes: {p.excluded.join(", ").toLowerCase()}.</p>
            <div className="flex justify-end"><button type="button" data-testid="report-generate" onClick={gen} disabled={busy || !sel.length} className="pp-btn-dark inline-flex items-center gap-1"><Download size={14} /> {busy ? "Generating…" : "Generate PDF"}</button></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
